/**
 * The reference letter forms, looked up by vocabulary class id.
 *
 * The generated table already keys on the append-only vocab class id, and `TokenOut.letter_class`
 * and `VocabEntryOut.id` are that same id — so putting the Hismaic form beside the transliteration
 * is a join, not a new data source. Nothing here fetches, and nothing here writes: `glyphOrientation`
 * decides how a form is *drawn* for a reading direction and stops there. What a **box** records is
 * its `chirality`, which nothing derives from a reading direction — see `draft.ts`.
 *
 * The honesty constraint the drill enforces holds here too, and matters more: **a class with no
 * form never gets one invented.** `illegible` and `unknown` are `glyph: null` and always will be —
 * a glyph aligning to a non-letter is a hard failure in `glyph_templates.py` — so callers get
 * `null` and must render a gap. See `GlyphImage.svelte`, which is the only component allowed to
 * make that decision.
 *
 * The forms are OCIANA's font renderings: idealized exemplars, a reference for reading the
 * transliteration, not a picture of any particular stone.
 */

import { ALPHABET, ALPHABET_SOURCE, type AlphabetEntry } from './alphabet.generated';
import type { ReadingDirection } from './api/types';

const BY_CLASS_ID: ReadonlyMap<number, AlphabetEntry> = new Map(
  ALPHABET.map((entry) => [entry.id, entry]),
);

/** The stored glyph's aspect ratio, read from the artifact rather than hardcoded. */
export const GLYPH_ASPECT = ALPHABET_SOURCE.glyphWidth / ALPHABET_SOURCE.glyphHeight;

/** The table row for a vocabulary class, or `undefined` if the id is not one. */
export function glyphEntry(letterClass: number): AlphabetEntry | undefined {
  return BY_CLASS_ID.get(letterClass);
}

/**
 * The reference form as a PNG data URI, or `null` when there is none.
 *
 * `null` covers both "this class has no form" and "this is not a class", because a caller can do
 * nothing different about the two: either way there is no form to draw.
 */
export function glyphUri(letterClass: number): string | null {
  return BY_CLASS_ID.get(letterClass)?.glyph ?? null;
}

/**
 * Whether a class's reference form has a handedness worth recording.
 *
 * **Three-valued, and the third value is not a falsy version of the other two.** The artifact
 * measures each stored form against its own mirror, so:
 *
 * | value | meaning | what a box on the class is born as |
 * | --- | --- | --- |
 * | `'chiral'` | the form and its mirror are different pictures | `null` — nobody has looked |
 * | `'symmetric'` | they are the same picture, so there is no answer to give | `'symmetric'` |
 * | `null` | **unmeasured** — the repo has no form for this class at all | `null` |
 *
 * `null` is `illegible` and `unknown`, which have no reference form and never will. Reading it
 * as `'chiral'` would put "reference or mirrored?" in front of somebody boxing a lacuna;
 * reading it as `'symmetric'` would stamp a measurement on a class nothing has measured. It is
 * neither, and the callers below branch on all three.
 *
 * The disposition is measured from the **stored bitmap**, so it is unaffected by which of the
 * two physical forms that bitmap turns out to be (§12 F1): a form and its mirror are equally
 * far apart whichever way round they are.
 */
export function letterChirality(letterClass: number): 'chiral' | 'symmetric' | null {
  return BY_CLASS_ID.get(letterClass)?.chirality ?? null;
}

/** True only for a class measured to read the same either way. Unmeasured is not symmetric. */
export function isSymmetric(letterClass: number): boolean {
  return letterChirality(letterClass) === 'symmetric';
}

/** The measured distance from a class's form to its own mirror, or `null` when unmeasured. */
export function mirrorInvariance(letterClass: number): number | null {
  return BY_CLASS_ID.get(letterClass)?.mirrorInvariance ?? null;
}

/** The rendered width for a given height, preserving the stored 15x20. */
export function glyphWidthFor(height: number): number {
  return Math.round(height * GLYPH_ASPECT);
}

/**
 * How the reference form is turned for a record's reading direction.
 *
 * Two independent facts, because that is what the script does:
 *
 * - **`mirrored`** — the writing runs *against* the reference. Right-to-left is this project's
 *   reference orientation, so left-to-right mirrors; and bottom-to-top is to top-to-bottom what
 *   left-to-right is to right-to-left, so it mirrors too. This is the same rule the corpus's own
 *   boustrophedon convention states: a line that turns and runs back the other way is mirrored,
 *   not turned upside down.
 * - **`turned`** — the writing runs *vertically*, so the whole line is a quarter turn off the
 *   reference. One quarter turn covers both vertical directions: rotating the reference so its
 *   leftward travel points **down** gives top-to-bottom, and bottom-to-top is that mirrored.
 *
 * So the four are one rule applied twice, not four cases:
 *
 * | direction | mirrored | turned | transform |
 * | --- | --- | --- | --- |
 * | `rtl` (reference) | no | no | — |
 * | `ltr` | yes | no | `scaleX(-1)` |
 * | `ttb` | no | yes | `rotate(-90deg)` |
 * | `btt` | yes | yes | `rotate(-90deg) scaleX(-1)` |
 *
 * `angle` and an unset direction get the reference form untouched: a baseline's slope is not a
 * quarter turn, and guessing is worse than showing the form the caption names.
 *
 * **Not `readingFlow`, either.** That one says which way the *strip* runs, and it measures from
 * the DOM's order (left to right, top to bottom) rather than from the reference form's `rtl`. So
 * `rtl` reverses the strip and leaves the glyph alone, while `ttb` turns the glyph and leaves the
 * strip's order alone. The two functions agree on `ltr` and `btt` and disagree on the other two,
 * which is why they stay two functions — see the table on `readingFlow` in `draft.ts`.
 *
 * **This is display, not data.** It decides how a PNG is drawn; a box's stored `chirality` is a
 * judgement about a glyph on a stone and is derived from nothing, least of all from this.
 */
export interface GlyphOrientation {
  /** Flip across the letter's own vertical axis — the writing runs against the reference. */
  mirrored: boolean;
  /** Quarter turn (`rotate(-90deg)`) — the writing runs vertically. */
  turned: boolean;
}

export function glyphOrientation(direction: ReadingDirection | null): GlyphOrientation {
  switch (direction) {
    case 'ltr':
      return { mirrored: true, turned: false };
    case 'ttb':
      return { mirrored: false, turned: true };
    case 'btt':
      return { mirrored: true, turned: true };
    default:
      return { mirrored: false, turned: false };
  }
}

/**
 * What orientation the forms are being shown in, in words.
 *
 * The reference orientation is a convention this project adopted, not settled epigraphy, so the
 * UI states which way it is showing rather than letting the annotator assume. An unset direction
 * is called out as unset: the reader is looking at the reference form because nobody has said
 * which way this text runs, and that is worth knowing while deciding whether a letter is mirrored.
 */
export function orientationNote(direction: ReadingDirection | null): string {
  switch (direction) {
    case 'ltr':
      return 'forms mirrored for left-to-right';
    case 'rtl':
      return 'reference forms (right-to-left)';
    case 'ttb':
      return 'reference forms turned for top-to-bottom';
    case 'btt':
      return 'reference forms turned for bottom-to-top';
    case 'angle':
      return 'reference forms (right-to-left) — an angled baseline gets no turn';
    default:
      return 'reference forms (right-to-left) — reading direction not set';
  }
}
